import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Camera, Loader2, Check, X, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OCR Camera Component for extracting data from photos
 * @param {string} type - 'odometer' or 'fuel'
 * @param {function} onDataExtracted - Callback with extracted data
 */
export const OcrCamera = ({ type = 'odometer', onDataExtracted, buttonText, buttonClassName }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const fileInputRef = useRef(null);

  const handleCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un\'immagine');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64Full = event.target.result;
      const base64Data = base64Full.split(',')[1];
      setPreviewImage(base64Full);
      
      // Process with OCR
      setIsProcessing(true);
      setExtractedData(null);
      
      try {
        const token = localStorage.getItem('vault_token');
        const endpoint = type === 'odometer' ? '/api/ocr/odometer' : '/api/ocr/fuel-receipt';
        
        const response = await fetch(`${BACKEND_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ image_base64: base64Data })
        });

        if (!response.ok) {
          throw new Error('Errore OCR');
        }

        const result = await response.json();
        
        if (result.success) {
          setExtractedData(result);
          
          if (type === 'odometer' && result.km !== null) {
            toast.success(`Rilevati ${result.km.toLocaleString()} km`);
          } else if (type === 'fuel') {
            const parts = [];
            if (result.liters) parts.push(`${result.liters}L`);
            if (result.total_cost) parts.push(`${result.total_cost.toFixed(2)} CHF`);
            if (parts.length > 0) {
              toast.success(`Rilevato: ${parts.join(', ')}`);
            }
          }
        } else {
          toast.error(result.error || 'Impossibile leggere i dati');
        }
      } catch (error) {
        console.error('OCR error:', error);
        toast.error('Errore durante la lettura');
      } finally {
        setIsProcessing(false);
      }
    };
    reader.readAsDataURL(file);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirm = () => {
    if (extractedData && onDataExtracted) {
      onDataExtracted(extractedData);
    }
    handleReset();
  };

  const handleReset = () => {
    setPreviewImage(null);
    setExtractedData(null);
  };

  // If we have a preview, show confirmation UI
  if (previewImage) {
    return (
      <div className="space-y-3">
        <div className="relative">
          <img 
            src={previewImage} 
            alt="Preview" 
            className="w-full h-32 object-cover rounded border border-[#262626]"
          />
          {isProcessing && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded">
              <div className="text-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#FF5F1F] mx-auto mb-2" />
                <span className="text-sm text-white">Analisi in corso...</span>
              </div>
            </div>
          )}
        </div>
        
        {extractedData && (
          <div className="bg-[#1a1a1a] border border-[#262626] rounded p-3">
            <p className="text-xs text-[#666] mb-2">Dati rilevati:</p>
            {type === 'odometer' ? (
              <div className="flex items-center justify-between">
                <span className="text-[#9CA3AF]">Chilometri:</span>
                <span className="text-white font-mono text-lg">
                  {extractedData.km !== null ? `${extractedData.km.toLocaleString()} km` : 'Non rilevato'}
                </span>
              </div>
            ) : (
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#9CA3AF]">Litri:</span>
                  <span className="text-white font-mono">
                    {extractedData.liters !== null ? `${extractedData.liters} L` : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#9CA3AF]">Totale:</span>
                  <span className="text-white font-mono">
                    {extractedData.total_cost !== null ? `${extractedData.total_cost.toFixed(2)} CHF` : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#9CA3AF]">Prezzo/L:</span>
                  <span className="text-white font-mono">
                    {extractedData.price_per_liter !== null ? `${extractedData.price_per_liter.toFixed(3)} CHF` : '-'}
                  </span>
                </div>
              </div>
            )}
            <p className="text-xs text-[#666] mt-2">
              Confidenza: {extractedData.confidence === 'high' ? '🟢 Alta' : extractedData.confidence === 'medium' ? '🟡 Media' : '🔴 Bassa'}
            </p>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            type="button"
            onClick={handleReset}
            variant="outline"
            className="flex-1 border-[#262626] text-[#9CA3AF]"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Riprova
          </Button>
          {extractedData && (
            <Button
              type="button"
              onClick={handleConfirm}
              className="flex-1 bg-[#FF5F1F] hover:bg-[#FF5F1F]/80"
              disabled={type === 'odometer' ? extractedData.km === null : (!extractedData.liters && !extractedData.total_cost)}
            >
              <Check className="w-4 h-4 mr-2" />
              Usa dati
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Default: show capture button
  return (
    <div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleCapture}
        accept="image/*"
        capture="environment"
        className="hidden"
      />
      <Button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className={buttonClassName || "w-full bg-[#262626] hover:bg-[#363636] text-white"}
      >
        <Camera className="w-4 h-4 mr-2" />
        {buttonText || (type === 'odometer' ? 'Scatta foto contachilometri' : 'Foto display pompa')}
      </Button>
    </div>
  );
};
